// Garment (custom apparel) form and quote ticket. Inputs bind to state with
// data-path; buttons dispatch data-action. See nms-quote.js for the wiring.

import { esc, attrs, options, usd, plural } from "./html.js";
import { rawBasePrice, tierUnitPrice } from "../engine/garments.js";

const ROSTER_SOFT_PROMPT_ROW = 15;

export const newDecal = (placement = "front") => ({ placement, oversized: false, widthIn: "", heightIn: "", note: "", art: null });

export const newGarmentLine = (cfg) => ({
  id: Math.random().toString(36).slice(2, 9),
  productId: cfg.catalog.products[0]?.id ?? "",
  color: cfg.catalog.products[0]?.colors?.[0] ?? "",
  customWholesale: "",
  customerSupplied: false,
  kind: "bulk",
  decals: [newDecal()],
  sizes: {},
  roster: [{ customer: "", size: "", color: "", name: "", number: "", qty: 1 }],
  rosterPromptDismissed: false,
});

export const newGarmentState = (cfg) => ({
  lines: [newGarmentLine(cfg)],
  bagging: false,
  artworkPolicy: "as-is",
  rush: "standard",
  delivery: { method: "pickup", zip: "" },
  copyright: false,
});

/** Convert widget state into the engine's order shape. */
export const garmentOrder = (s, mode, cfg) => ({
  mode,
  lines: s.lines.map((l) => ({
    ...l,
    productId: l.customWholesale !== "" || l.customerSupplied ? null : l.productId,
    customWholesale: mode === "internal" && l.customWholesale !== "" ? Number(l.customWholesale) : null,
    customerSupplied: mode === "internal" && l.customerSupplied,
    label: lineLabel(l, cfg),
  })),
  bagging: s.bagging,
  artworkPolicy: s.artworkPolicy,
  rush: s.rush,
  delivery: s.delivery,
});

function lineLabel(line, cfg) {
  if (line.customerSupplied) return "Customer-supplied garment";
  if (line.customWholesale !== "") return `Custom garment ($${Number(line.customWholesale).toFixed(2)} wholesale)`;
  return cfg?.catalog.products.find((p) => p.id === line.productId)?.label ?? "Garment";
}

const radioCards = (name, path, list, value, type) =>
  `<div class="choices">${list
    .map(
      (c) => `<label class="choice" ${attrs({ "data-requires-local": c.local, hidden: c.hidden })}>
        <input type="radio" ${attrs({ name, "data-path": path, "data-type": type, value: c.id, checked: c.id === value, disabled: c.disabled })}>
        <strong>${esc(c.label)}</strong>${c.price ? `<span class="price">${esc(c.price)}</span>` : ""}${c.desc ? `<span>${esc(c.desc)}</span>` : ""}
      </label>`,
    )
    .join("")}</div>`;

function decalView(d, li, di, cfg, uid) {
  const p = `g.lines.${li}.decals.${di}`;
  const placement = cfg.placements.find((x) => x.id === d.placement);
  const art = d.art;
  return `<div class="decal">
    <div class="decal-head"><span>Decal ${di + 1}${di === 0 ? " · first" : ""}</span>
      ${di > 0 ? `<button class="btn ghost" data-action="remove-decal" data-line="${li}" data-decal="${di}" aria-label="Remove decal ${di + 1}">Remove</button>` : ""}</div>
    <div class="row">
      <label class="field">Placement
        <select data-path="${p}.placement">${options(cfg.placements.map((x) => ({ ...x, label: x.sleeve ? `${x.label} (+$${cfg.constants.sleeveDecalSurcharge})` : x.label })), d.placement)}</select>
      </label>
      ${placement?.requiresNote ? `<label class="field">Describe the exact placement
        <input type="text" data-path="${p}.note" value="${esc(d.note)}" placeholder="e.g. lower back hem, nape of neck" ${attrs({ "aria-invalid": !d.note.trim() })}></label>` : ""}
    </div>
    <label class="check"><input type="checkbox" data-path="${p}.oversized" data-type="bool" ${attrs({ checked: d.oversized })}>
      <span>I need an oversized decal larger than 10"x12" (+$${cfg.constants.oversizedDecalSurcharge.toFixed(2)})</span></label>
    ${d.oversized ? `<div class="row">
      <label class="field">Requested width (in)<input type="number" min="1" step="0.1" data-path="${p}.widthIn" value="${esc(d.widthIn)}"></label>
      <label class="field">Requested height (in)<input type="number" min="1" step="0.1" data-path="${p}.heightIn" value="${esc(d.heightIn)}"></label>
    </div>` : ""}
    <label class="drop" data-drop="${li}:${di}">
      <input type="file" accept="image/png" data-upload="${li}:${di}" id="${uid}-art-${li}-${di}">
      ${art?.url ? `<img class="thumb" src="${esc(art.url)}" alt="">` : `<span class="thumb" aria-hidden="true"></span>`}
      <span>${art
        ? art.ok
          ? `<span class="ok">✓ ${esc(art.name)}: ${art.widthIn.toFixed(2)}" × ${art.heightIn.toFixed(2)}" at ${Math.round(art.dpi)} DPI</span>`
          : `<span class="error">${esc(art.error)}</span>`
        : `<b>Upload artwork</b> (optional now, needed before production)<br><span class="hint">Transparent PNG, 300 DPI or higher. Drop it here or click to browse.</span>`}</span>
    </label>
  </div>`;
}

function sizeGrid(line, li, cfg) {
  return `<div class="sizes">${cfg.sizes
    .map(
      (s) => `<label class="size">${esc(s.id)}${s.upcharge ? ` <span class="up">+$${s.upcharge}</span>` : ""}
        <input type="number" min="0" step="1" inputmode="numeric" data-path="g.lines.${li}.sizes.${s.id}" data-type="int" value="${esc(line.sizes[s.id] ?? "")}" aria-label="${esc(s.label)} quantity"></label>`,
    )
    .join("")}</div>`;
}

function rosterGrid(line, li, cfg, rowErrors = []) {
  const product = cfg.catalog.products.find((p) => p.id === line.productId);
  const colors = line.customerSupplied || line.customWholesale !== "" ? null : product?.colors;
  const showPrompt = line.roster.length >= ROSTER_SOFT_PROMPT_ROW && !line.rosterPromptDismissed;
  return `
    ${showPrompt ? `<div class="banner" role="status"><div><strong>Have a large team?</strong>
      Typing out long rosters can be tedious! You can save your progress and avoid losing your work by using our
      <button class="link" data-action="roster-template">Downloadable Excel Template</button>. Fill it out on your own time and upload it here to instantly build your cart.</div>
      <button class="btn ghost" style="color:inherit" data-action="dismiss-roster-prompt" data-line="${li}" aria-label="Dismiss">✕</button></div>` : ""}
    <div class="roster-wrap"><table class="roster">
      <thead><tr><th>Recipient</th><th>Size</th><th>Color</th><th>Player name</th><th>No.</th><th>Qty</th><th></th></tr></thead>
      <tbody>${line.roster
        .map((r, ri) => {
          const errs = rowErrors[ri] ?? [];
          const bad = (f) => errs.some((e) => e.field === f);
          const p = `g.lines.${li}.roster.${ri}`;
          return `<tr class="${errs.length ? "bad" : ""}" title="${esc(errs.map((e) => e.message).join(" "))}">
            <td><input type="text" data-path="${p}.customer" value="${esc(r.customer)}" aria-label="Recipient" ${attrs({ "aria-invalid": bad("customer") })}></td>
            <td><select data-path="${p}.size" aria-label="Size" ${attrs({ "aria-invalid": bad("size") })}>${options(cfg.sizes.map((s) => ({ id: s.id, label: s.id })), r.size, "—")}${r.size && !cfg.sizes.some((s) => s.id === r.size) ? `<option selected value="${esc(r.size)}">${esc(r.size)} ?</option>` : ""}</select></td>
            <td>${colors ? `<select data-path="${p}.color" aria-label="Color" ${attrs({ "aria-invalid": bad("color") })}>${options(colors.map((c) => ({ id: c, label: c })), r.color, "—")}${r.color && !colors.some((c) => c.toLowerCase() === r.color.toLowerCase()) ? `<option selected value="${esc(r.color)}">${esc(r.color)} ?</option>` : ""}</select>` : `<input type="text" data-path="${p}.color" value="${esc(r.color)}" aria-label="Color">`}</td>
            <td><input type="text" maxlength="15" data-path="${p}.name" value="${esc(r.name)}" aria-label="Player name" ${attrs({ "aria-invalid": bad("name") })}></td>
            <td><input type="text" inputmode="numeric" maxlength="2" style="width:3.2rem" data-path="${p}.number" value="${esc(r.number)}" aria-label="Player number" ${attrs({ "aria-invalid": bad("number") })}></td>
            <td><input type="number" min="1" step="1" style="width:4rem" data-path="${p}.qty" data-type="int" value="${esc(r.qty)}" aria-label="Quantity" ${attrs({ "aria-invalid": bad("qty") })}></td>
            <td>${line.roster.length > 1 ? `<button class="btn ghost" data-action="remove-roster-row" data-line="${li}" data-row="${ri}" aria-label="Remove row ${ri + 1}">✕</button>` : ""}</td>
          </tr>`;
        })
        .join("")}</tbody></table></div>
    <div class="actions">
      <button class="btn" data-action="add-roster-row" data-line="${li}">+ Add another player</button>
      <label class="btn" style="position:relative">Upload roster CSV<input type="file" accept=".csv,text/csv" data-roster-upload="${li}" style="position:absolute;inset:0;opacity:0;cursor:pointer"></label>
      <button class="btn ghost" data-action="roster-template">Download template</button>
    </div>
    <p class="hint">Roster orders include personalization (+$${cfg.constants.personalizationFee.toFixed(2)}) and required kitting &amp; bagging (+$${cfg.constants.baggingFee.toFixed(2)}) on every garment.</p>`;
}

function lineView(line, li, cfg, mode, quoteLine, uid) {
  const p = `g.lines.${li}`;
  const product = cfg.catalog.products.find((x) => x.id === line.productId);
  const staffCustom = mode === "internal" && (line.customWholesale !== "" || line.customerSupplied);
  return `<div class="item">
    <div class="item-head"><span class="tag">Item ${li + 1}</span>
      <div style="display:flex;gap:.5rem;align-items:center">
        <div class="seg" role="group" aria-label="Order type">
          <button data-action="set-kind" data-line="${li}" data-kind="bulk" aria-pressed="${line.kind === "bulk"}">Bulk sizes</button>
          <button data-action="set-kind" data-line="${li}" data-kind="roster" aria-pressed="${line.kind === "roster"}">Roster / names</button>
        </div>
        ${li > 0 ? `<button class="btn ghost" data-action="remove-line" data-line="${li}">Remove</button>` : ""}
      </div>
    </div>
    ${mode === "internal" ? `<div class="row">
      <label class="field">Custom wholesale cost ($)<input type="number" min="0" step="0.01" data-path="${p}.customWholesale" value="${esc(line.customWholesale)}" placeholder="Leave blank to use catalog" ${attrs({ disabled: line.customerSupplied })}></label>
      <label class="check" style="align-self:end;padding-bottom:.6rem"><input type="checkbox" data-path="${p}.customerSupplied" data-type="bool" ${attrs({ checked: line.customerSupplied })}><span>Customer-supplied blank (no wholesale cost, +$${cfg.constants.customerGarmentHandlingFee.toFixed(2)} handling)</span></label>
    </div>` : ""}
    ${staffCustom ? "" : `<div class="row">
      <label class="field">Garment<select data-path="${p}.productId">${options(cfg.catalog.products, line.productId)}</select></label>
      ${line.kind === "bulk" ? `<label class="field">Color<select data-path="${p}.color">${options((product?.colors ?? []).map((c) => ({ id: c, label: c })), line.color)}</select></label>` : ""}
    </div>`}
    <div>
      <div class="section-title" style="font-size:.85rem;margin-bottom:.5rem">Decals</div>
      <div style="display:grid;gap:.9rem">${line.decals.map((d, di) => decalView(d, li, di, cfg, uid)).join("")}</div>
      <div class="actions" style="margin-top:.6rem"><button class="btn" data-action="add-decal" data-line="${li}">+ Add decal (+$${cfg.constants.additionalDecalFee.toFixed(2)})</button></div>
      <p class="hint" style="margin-top:.5rem">We scale artwork to standard placements: <b>front/back</b> 10–12" at the largest point, <b>chest</b> 3–5" wide, <b>sleeves</b> within 3–5".</p>
    </div>
    <div>
      <div class="section-title" style="font-size:.85rem;margin-bottom:.5rem">${line.kind === "roster" ? "Roster" : "Sizes &amp; quantities"}</div>
      ${line.kind === "roster" ? rosterGrid(line, li, cfg, quoteLine?.rowErrors) : sizeGrid(line, li, cfg)}
    </div>
  </div>`;
}

export function garmentForm(state, cfg, mode, quote, uid) {
  const s = state.g;
  const k = cfg.constants;
  const zip = String(s.delivery.zip ?? "").trim().slice(0, 5);
  const local = cfg.delivery.courierZips.includes(zip);
  const deliveryChoices = cfg.delivery.methods
    .map((m) => ({ id: m.id, label: m.label, price: m.carrierCalculated ? "Calculated by carrier" : m.price ? `${usd(m.price)}${m.minimum ? " minimum" : ""}` : "Free",
      local: m.requiresLocalZip, hidden: m.requiresLocalZip && !local && s.delivery.method !== m.id }));
  return `
  <fieldset><legend><span class="num">01</span>Garments</legend>
    <div style="display:grid;gap:1rem">${s.lines.map((l, i) => lineView(l, i, cfg, mode, quote?.lines?.[i], uid)).join("")}</div>
    <div class="actions" style="margin-top:.8rem"><button class="btn" data-action="add-line">+ Add another garment or design</button></div>
  </fieldset>

  <fieldset><legend><span class="num">02</span>Artwork &amp; production policy</legend>
    ${radioCards("artwork", "g.artworkPolicy", [
      { id: "as-is", label: 'Print "As-Is"', price: "$0.00", desc: "My file is print-ready (300 DPI, transparent background). I accept responsibility for low-quality uploads." },
      { id: "proof", label: 'Physical "Flight Check" proof', price: `+${usd(k.flightCheckProofFee)}`, desc: "We press one DTF sample for you to approve. Adds 3–5 days. Unapproved proofs are held 10 business days." },
      { id: "design", label: "Professional design support", price: `${usd(k.designSupportHourly)}/hour`, desc: "A designer cleans up or vectorizes your art and sends a time estimate first." },
    ], s.artworkPolicy)}
  </fieldset>

  <fieldset><legend><span class="num">03</span>Packaging &amp; speed</legend>
    ${radioCards("bagging", "g.bagging", [
      { id: "false", label: "Bulk box delivery", price: "Free", desc: "Folded and packed together." },
      { id: "true", label: "White-glove kitting & bagging", price: `+${usd(k.baggingFee)}/garment`, desc: "Individually bagged, sealed, and labeled by size (and player name). Required for rosters." },
    ], String(s.bagging), "bool")}
    <div style="height:.6rem"></div>
    ${radioCards("rush", "g.rush", cfg.rush.map((r) => ({ id: r.id, label: r.label.replace(/ \+\d+%$/, ""), price: r.multiplier > 1 ? `+${Math.round((r.multiplier - 1) * 100)}% of order` : "Included" })), s.rush)}
  </fieldset>

  <fieldset><legend><span class="num">04</span>Delivery</legend>
    <div class="row" style="margin-bottom:.6rem">
      <label class="field">Delivery ZIP code<input type="text" inputmode="numeric" maxlength="10" data-path="g.delivery.zip" value="${esc(s.delivery.zip)}" placeholder="e.g. 22630"></label>
    </div>
    ${radioCards("delivery", "g.delivery.method", deliveryChoices, s.delivery.method)}
    <p class="hint" style="margin-top:.4rem">Local courier delivery to your school's front office or business appears for Warren County ZIP codes.</p>
  </fieldset>

  <fieldset><legend><span class="num">05</span>Copyright</legend>
    <label class="check"><input type="checkbox" data-path="g.copyright" data-type="bool" ${attrs({ checked: s.copyright })}>
      <span><b>I verify that I have the legal right to use this artwork.</b> I either own the intellectual property rights to the uploaded design or have explicit, documented permission from the copyright/trademark owner to reproduce and distribute it. I agree to hold National Media Services, Inc. harmless from any legal claims, damages, or liabilities arising from copyright or trademark infringement related to this order.</span></label>
  </fieldset>`;
}

function ladder(line, cfg, mode, quoteLine) {
  const base = rawBasePrice(line, cfg, mode);
  if (base.errors.length) return "";
  const current = quoteLine?.tier?.label;
  return `<table class="breaks"><thead><tr><th>Qty</th><th>Each*</th></tr></thead><tbody>${cfg.quantityTiers
    .map((t) => `<tr class="${t.label === current ? "current" : ""}"><td>${esc(t.label)}</td><td>${usd(tierUnitPrice(base.amount, t.min, cfg).unit)}</td></tr>`)
    .join("")}</tbody></table>`;
}

export function garmentTicket(quote, state, cfg, mode) {
  const order = garmentOrder(state.g, mode, cfg);
  const lineBlocks = quote.lines
    .map((l, i) => {
      if (!l.qty) return `<div><div class="group-title">${esc(l.label)}</div><p class="hint">Enter sizes to see pricing.</p></div>`;
      return `<div>
        <div class="group-title">${esc(l.label)} × ${l.qty}</div>
        <dl>
          ${l.tier ? `<dt>Quantity tier</dt><dd>${esc(l.tier.label)}</dd>` : ""}
          ${l.sizeGroups.map((g) => `<dt>${esc(g.size)} × ${g.qty} @ ${usd(g.unit)}</dt><dd>${usd(g.total)}</dd>`).join("")}
          ${l.addons.length ? `<dt style="grid-column:1/-1;font-size:.75rem">Includes ${l.addons.map((a) => `${esc(a.label.toLowerCase())} ${usd(a.amount)}`).join(", ")} per garment</dt>` : ""}
        </dl>
        ${mode === "internal" ? `<details style="margin-top:.4rem"><summary class="hint">Raw base ${usd(l.rawBase)} → tier ${usd(l.tierUnit)}</summary><dl>${l.rawBaseParts.map((p) => `<dt>${esc(p.label)}</dt><dd>${usd(p.amount)}</dd>`).join("")}</dl></details>` : ""}
        <details style="margin-top:.4rem" ${quote.lines.length === 1 ? "open" : ""}><summary class="hint">Price breaks for this item</summary>${ladder(order.lines[i], cfg, mode, l)}<p class="hint">*Before size upcharges and add-ons.</p></details>
      </div>`;
    })
    .join('<div class="sep"></div>');

  return `
    ${lineBlocks}
    <div class="sep"></div>
    <dl>
      <dt>Items subtotal</dt><dd>${usd(quote.itemsSubtotal)}</dd>
      ${quote.orderFees.map((f) => `<dt>${esc(f.label)}</dt><dd>${usd(f.amount)}</dd>`).join("")}
      ${quote.rush.amount ? `<dt>${esc(quote.rush.label)}</dt><dd>${usd(quote.rush.amount)}</dd>` : ""}
      ${quote.delivery ? `<dt>${esc(quote.delivery.label)}${quote.delivery.note ? `<br><span style="font-size:.72rem">${esc(quote.delivery.note)}</span>` : ""}</dt><dd>${quote.delivery.amount == null ? "TBD" : usd(quote.delivery.amount)}</dd>` : ""}
    </dl>
    ${quote.garmentCount ? `<p class="hint">${plural(quote.garmentCount, "garment")}${quote.expiresOn ? ` · quote valid until ${esc(quote.expiresOn)}` : ""}</p>` : ""}`;
}
